import { onMessage } from 'firebase/messaging';
import React, { createContext, useContext, useEffect, useMemo, useReducer } from 'react';
import { appNotificationsHandlers, appNotificationsInitialState, appNotificationsReducer } from '../../cnr/reducers/AppNotificationsReducer';
import { _notificationsCollection } from '../../firestoreData/notifications/notificationsData';
import { AppParentContext } from './AppParentContext';
import { AppContext } from './AppProvider';
import { AppUserContext } from './AppUserContext';
import { DatabaseContext } from './DatabaseContext';
import { PageContext } from './PageContext';
import { PapsContext } from './PapsContext';
import { CurrentSettingsContext } from './SettingsDataContext';
import { useFirestore } from './useHelpers/useFirestore';
import { useAppUserAccess } from './useHelpers/useAppUserAccess';
import { useNotifications } from './useHelpers/useNotifications';

/** Notifications for the event
 * @ParentProvider - NOT AVAILIBLE
 */
export const AppNotificationsContext = createContext();

/** Global Files for the Event */
const AppNotificationsProvider = (props) => {

  // appParentContext
  const appParentContext = useContext(AppParentContext)
  const { fn_cls } = appParentContext ?? {}

  const appContext = useContext(AppContext)
  const { app_state } = appContext ?? {}
  const { mainMessaging } = app_state ?? {}

  // databaseContext
  const databaseContext = useContext(DatabaseContext)
  const { database_fns } = databaseContext ?? {}

  // appUserContext
  const appUserContext = useContext(AppUserContext);
  const { appUser_state } = appUserContext ?? {}
  const { appUser, appUsers } = appUser_state ?? {}
  const appUserAccess = useAppUserAccess()

  // papsContext 
  const papsContext = useContext(PapsContext)
  const { paps_state } = papsContext ?? {}
  const { pathViews, showStatus } = paps_state ?? {}

  // currentSettingsContext
  const currentSettingsContext = useContext(CurrentSettingsContext)
  const { currentSettings_state } = currentSettingsContext ?? {}
  const { aps_global, aps_viewItems, aps_views, aps_appUserSettings } = _.get(currentSettings_state, 'gvviSettings', {});

  // pageContext 
  const pageContext = useContext(PageContext)
  const { page_fns, navigate } = pageContext ?? {}
  const { projectModules, appNotifications, logging } = aps_global ?? {}
  const { allowNotifications: allowNotifications_g } = projectModules ?? {}
  const { allowNotifications, allowPushNotifications, pushNotificationsActive, pushActiveToSelf, allowNotificationTopics } = appNotifications ?? {}

  const _allowNotifications = allowNotifications_g && allowNotifications

  // const { getAppNotifications } = useNotifications()
  // const { data: _notifications } = getAppNotifications()
  const { data: _notifications } = useFirestore({ items: _allowNotifications && pathViews?.events ? [_notificationsCollection] : null })

  // reducer
  const initState = appNotificationsInitialState({ database_fns, fn_cls, appUser, appUsers, appNotifications, appUserAccess, aps_appUserSettings, pathViews, page_fns, logging, navigate, aps_viewItems, allowNotificationTopics })
  const [appNotifications_state, notifications_dispatch] = useReducer(appNotificationsReducer, initState);

  const appNotifications_handlers = useMemo(
    () => appNotificationsHandlers(notifications_dispatch),
    [notifications_dispatch]
  );

  const { handleAppUserNotifications, handleScheduleNotifications, handleLoadNotifications } = appNotifications_handlers
  const { notifications_app } = appNotifications_state

  if (showStatus && notifications_app) { console.log('appNotifications_state ready', appNotifications_state) }

  useEffect(() => {

    if (!mainMessaging) {
      console.log('mainMessaging is not initialized!');
      return;
    }

    // This runs in your React app when the user has it open (foreground).
    const unsubscribe = onMessage(mainMessaging, (payload) => {
      // Forward to SW so it shows a system notification
      if (navigator.serviceWorker?.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: "SHOW_NOTIFICATION",
          payload,
        });
      }
      // Optional: Show custom notification
      // if (Notification.permission === 'granted') {
      //   new Notification(title, { body });
      // } 
      appNotifications_handlers.handleNewForegroundNotification(payload);
    });

    return unsubscribe;
  }, [mainMessaging]);

  useEffect(
    () => {
      if (!_.isEmpty(_notifications)) {
        handleLoadNotifications(_notifications)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [_notifications] // does not re-rednder unless uivi has changed
  )

  useEffect(() => {
    if (notifications_app && appUserAccess) {
      handleAppUserNotifications(appUserAccess)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appUserAccess, notifications_app]);

  useEffect(() => {
    if (notifications_app && _allowNotifications && allowPushNotifications && (pushNotificationsActive || pushActiveToSelf)) {
      handleScheduleNotifications(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications_app]);

  const providerContext = () => <AppNotificationsContext.Provider value={{ appNotifications_state, appNotifications_handlers, appUser }}>
    {props.children}
  </AppNotificationsContext.Provider>

  return (
    <React.Fragment>
      {providerContext()}
    </React.Fragment>
  )
}

export default AppNotificationsProvider